import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { authApi, sessionsApi } from '@/api/endpoints';
import { api } from '@/api/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OnboardingTour, resetTourSeen } from '@/components/dashboard/onboarding-tour';
import { SetupChecklist } from '@/components/dashboard/setup-checklist';

export const Route = createFileRoute('/dashboard/')({ component: DashboardHomePage });

function DashboardHomePage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;

  if (!me) {
    return (
      <div className="space-y-6 max-w-5xl">
        <div className="h-8 bg-slate-100 rounded w-1/3 animate-pulse" />
      </div>
    );
  }

  if (isTherapist) return <TherapistHome me={me} />;

  if (!me.clientProfile) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Complete seu cadastro</CardTitle>
          <CardDescription>Preencha seus dados para começar a agendar sessões.</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/onboarding/client">
            <Button size="lg">Completar cadastro</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return <ClientHome me={me} />;
}

function TherapistHome({ me }: { me: any }) {
  const profile = me.therapistProfile;
  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: () => sessionsApi.list() });
  const { data: analytics } = useQuery({
    queryKey: ['my-analytics'],
    queryFn: () => api.get('therapists/me/analytics').json<any>(),
  });

  const upcomingCount = sessions.filter(
    (s: any) => s.status === 'CONFIRMED' && new Date(s.scheduledAt) >= new Date(),
  ).length;
  const pendingCount = sessions.filter((s: any) => s.status === 'PENDING').length;
  const externalDone = analytics?.externalSessionsDone ?? 0;

  const [tourKey, setTourKey] = useState(0);
  const replayTour = () => {
    resetTourSeen();
    setTourKey((k) => k + 1);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <OnboardingTour key={tourKey} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Olá, {me.name?.split(' ')[0]}!</h1>
          <p className="text-slate-500 text-sm mt-1">Aqui está o resumo do seu consultório</p>
        </div>
        <button
          type="button"
          onClick={replayTour}
          className="text-xs text-slate-400 hover:text-teal-600 font-medium whitespace-nowrap mt-1"
          title="Rever o tour guiado"
        >
          🧭 Rever tour
        </button>
      </div>

      {/* Promo de adesão — 3 primeiros meses sem comissão */}
      <FeeWaiverBanner deadline={profile.feeWaiverUntil} />

      {/* Checklist de setup — só aparece enquanto houver pendências */}
      <SetupChecklist />

      {/* O-09 — banner countdown da janela de importação de carteira */}
      <ExternalBillingWindowBanner deadline={profile.externalBillingAllowedUntil} />

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard label="Próximas sessões" value={upcomingCount} href="/dashboard/sessoes" />
        <StatCard label="Aguardando confirmação" value={pendingCount} href="/dashboard/sessoes" />
        <StatCard
          label="Avaliação média"
          value={profile.ratingAvg ? Number(profile.ratingAvg).toFixed(1) : '—'}
          suffix={profile.ratingAvg ? '⭐' : ''}
        />
      </div>

      {/* O-XX — separa atendimentos não-cobrados (externos/cortesia) da receita */}
      {externalDone > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-600">
              Atendimentos não-cobrados pela plataforma
            </CardTitle>
            <CardDescription className="text-xs">
              Sessões concluídas em modo "Pagamento direto" ou "Cortesia". Não geram receita
              nem entram em ciclo de faturamento — aparecem aqui só pra você acompanhar o uso da plataforma.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-800">{externalDone}</p>
            <p className="text-xs text-slate-400 mt-0.5">sessão{externalDone === 1 ? '' : 'ões'} concluída{externalDone === 1 ? '' : 's'}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Atalhos</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          <Link to="/dashboard/agenda">
            <Button variant="outline" className="w-full justify-start">📅 Ver agenda completa</Button>
          </Link>
          <Link to="/dashboard/perfil">
            <Button variant="outline" className="w-full justify-start">👤 Editar perfil</Button>
          </Link>
          <Link to="/dashboard/financeiro">
            <Button variant="outline" className="w-full justify-start">💰 Financeiro</Button>
          </Link>
          <Link to="/terapeutas/$id" params={{ id: profile.id }}>
            <Button variant="outline" className="w-full justify-start">👁️ Ver perfil público</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function ClientHome({ me }: { me: any }) {
  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: () => sessionsApi.list() });
  const { data: payments = [] } = useQuery({
    queryKey: ['my-payments'],
    queryFn: async () => {
      try { return await api.get('payments/me').json<any[]>(); } catch { return []; }
    },
  });

  // Só sessões futuras e ativas — canceladas/em disputa saem daqui
  // (mostrar elas induziria o cliente a achar que a sessão está de pé).
  const upcomingSessions = (sessions as any[])
    .filter((s) => s.status !== 'CANCELLED' && s.status !== 'DISPUTED')
    .filter((s) => new Date(s.scheduledAt) >= new Date())
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    .slice(0, 3);

  const pendingBatchPayments = (payments as any[]).filter(
    (p) => p.status === 'PENDING' && p.recurringScheduleId && !p.sessionId,
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Olá, {me.name?.split(' ')[0]}!</h1>
        <p className="text-slate-500 text-sm mt-1">Sua jornada de cuidado</p>
      </div>

      {pendingBatchPayments.length > 0 && (
        <div className="space-y-2">
          {pendingBatchPayments.map((pay: any) => (
            <div key={pay.id} className="flex items-center gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <span className="text-xl shrink-0">⚠️</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">Pagamento pendente</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {pay.recurringSchedule?.totalSessions ?? '?'} sessões ·{' '}
                  {Number(pay.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
              <Link
                to="/pagamento/$paymentId"
                params={{ paymentId: pay.id }}
                className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition whitespace-nowrap"
              >
                Pagar agora
              </Link>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Próximas sessões</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingSessions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 mb-4">Você ainda não tem sessões agendadas</p>
              <Link to="/terapeutas"><Button>Encontrar profissional</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingSessions.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                  <div>
                    <div className="font-medium">{s.therapist?.user?.name ?? 'Sessão'}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(s.scheduledAt).toLocaleString('pt-BR', { dateStyle: 'long', timeStyle: 'short' })}
                    </div>
                  </div>
                  <Link to="/dashboard/sessoes/$id" params={{ id: s.id }}>
                    <Button variant="outline" size="sm">Ver</Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, suffix, href }: {
  label: string; value: string | number; suffix?: string; href?: string;
}) {
  const inner = (
    <CardContent className={`p-5 ${href ? 'group' : ''}`}>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-extrabold text-slate-900 mt-1">
        {value}
        {suffix && <span className="text-base ml-1">{suffix}</span>}
      </div>
      {href && <div className="text-[11px] text-teal-600 font-medium mt-2 group-hover:underline">Ver →</div>}
    </CardContent>
  );

  if (href) {
    return (
      <Link to={href as any}>
        <Card className="hover:border-teal-300 hover:shadow-sm transition cursor-pointer">{inner}</Card>
      </Link>
    );
  }
  return <Card>{inner}</Card>;
}

/**
 * Promoção de adesão — mostra a contagem regressiva dos 3 meses sem taxa
 * da plataforma. Só aparece pra terapeutas que ainda estão dentro da
 * janela (feeWaiverUntil > now). Backfill não é feito, então terapeutas
 * antigos simplesmente não veem este banner.
 */
function FeeWaiverBanner({ deadline }: { deadline: string | null | undefined }) {
  if (!deadline) return null;
  const d = new Date(deadline);
  const daysLeft = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (daysLeft <= 0) return null;
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-4 flex items-start gap-3">
      <div className="text-2xl shrink-0">🎁</div>
      <div className="text-sm flex-1">
        <p className="font-bold text-amber-900">
          Promoção de adesão: <strong>0% de taxa</strong> por mais <strong>{daysLeft} dia{daysLeft === 1 ? '' : 's'}</strong>
        </p>
        <p className="text-xs mt-1 text-amber-800 leading-relaxed">
          Até <strong>{d.toLocaleDateString('pt-BR')}</strong>, você recebe <strong>100%</strong> do valor
          de cada sessão realizada — sem comissão da plataforma. Toda sessão agendada ou concluída
          nesta janela mantém a isenção, mesmo que você veja o repasse só no fechamento do ciclo seguinte.
        </p>
      </div>
    </div>
  );
}

/**
 * O-09 — Banner que mostra a contagem regressiva da janela de importação
 * de sessões já pagas (EXTERNAL_PAID). Some quando expirar pra não virar
 * banner residual ruidoso. Aparece amarelado nos últimos 7 dias.
 */
function ExternalBillingWindowBanner({ deadline }: { deadline: string | null | undefined }) {
  if (!deadline) return null;
  const d = new Date(deadline);
  const daysLeft = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (daysLeft <= 0) return null;
  const urgent = daysLeft <= 7;
  return (
    <div className={
      'rounded-xl border px-5 py-4 flex items-start gap-3 ' +
      (urgent ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200')
    }>
      <div className="text-xl">{urgent ? '⚠️' : 'ℹ️'}</div>
      <div className="text-sm">
        <p className={'font-semibold ' + (urgent ? 'text-amber-800' : 'text-teal-800')}>
          Você tem <strong>{daysLeft} dia{daysLeft === 1 ? '' : 's'}</strong> pra importar sua carteira já paga fora da plataforma.
        </p>
        <p className={'text-xs mt-1 ' + (urgent ? 'text-amber-700' : 'text-teal-700')}>
          Após {d.toLocaleDateString('pt-BR')}, pagamentos só via Nosso Ello (cartão ou PIX). Marque a opção
          "Já paga fora da plataforma" ao criar uma nova série pra um cliente que paga você diretamente.
        </p>
      </div>
    </div>
  );
}
